import { useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import type { LucideIcon } from 'lucide-react';
import { ROUTES } from '@/shared/constants/routes';
import { Icons } from '@/shared/constants/icons';
import { LAYOUT_ID, SPRING, motionSafe } from '@/shared/constants/motion';
import { useInboxBadges } from '@/shared/hooks/useInboxBadges';
import { useAttentionCount } from '@/shared/hooks/useAttention';
import { DESKTOP_QUERY, WIDE_QUERY, useMatchMedia } from '@/shared/hooks/useMatchMedia';
import { useNavStore } from '@/shared/stores/nav.store';
import { openItems, useCurrentPlan } from '@/shared/hooks/usePlan';
import { useAuthStore } from '@/shared/stores/auth.store';
import { useCanWrite } from '@/shared/hooks/useCanWrite';
import { canReadAnalytics, canReadInbox } from '@/shared/lib/role-access';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  onCompose: () => void;
  /** Opens the command palette. Only rendered in the phone drawer, where the top bar has no search. */
  onSearch?: () => void;
  /**
   * A white-label workspace that keeps "Powered by UniPostudio" on. Off for
   * one that switched it off, and for every workspace without white label,
   * where the whole app already is UniPostudio.
   */
  showPoweredBy?: boolean;
}

interface NavItem {
  key: string;
  path: string;
  icon: LucideIcon;
  labelKey: string;
}

/** A labelled section of the rail (DS04). The first has no heading. */
interface NavGroup {
  key: string;
  labelKey?: string;
  items: NavItem[];
}

const ITEM = {
  // Home: Overview and Attention merged (DS05); it carries the attention count.
  overview: { key: 'overview', path: ROUTES.OVERVIEW, icon: Icons.home, labelKey: 'nav.overview' },
  inbox: { key: 'inbox', path: ROUTES.INBOX, icon: Icons.inbox, labelKey: 'nav.inbox' },
  calendar: { key: 'calendar', path: ROUTES.CALENDAR, icon: Icons.calendar, labelKey: 'nav.calendar' },
  // The weekly plan (Task 2.3): its badge counts the items still to decide.
  plan: { key: 'plan', path: ROUTES.PLAN, icon: Icons.plan, labelKey: 'nav.plan' },
  posts: { key: 'posts', path: ROUTES.POSTS, icon: Icons.posts, labelKey: 'nav.posts' },
  approvals: { key: 'approvals', path: ROUTES.APPROVALS, icon: Icons.check, labelKey: 'nav.approvals' },
  media: { key: 'media', path: ROUTES.MEDIA, icon: Icons.media, labelKey: 'nav.media' },
  templates: { key: 'templates', path: ROUTES.TEMPLATES, icon: Icons.templates, labelKey: 'nav.templates' },
  analytics: { key: 'analytics', path: ROUTES.ANALYTICS, icon: Icons.analytics, labelKey: 'nav.analytics' },
  feeds: { key: 'feeds', path: ROUTES.FEEDS, icon: Icons.feeds, labelKey: 'nav.feeds' },
  // The brand profile is what the AI reads as its source of truth: it gets its own entry.
  brand: { key: 'brand', path: ROUTES.BRAND, icon: Icons.brand, labelKey: 'nav.brand' },
  channels: { key: 'channels', path: ROUTES.CHANNELS, icon: Icons.channels, labelKey: 'nav.channels' },
  settings: { key: 'settings', path: ROUTES.SETTINGS, icon: Icons.settings, labelKey: 'nav.settings' },
  docs: { key: 'docs', path: ROUTES.DOCS, icon: Icons.help, labelKey: 'nav.help' },
} satisfies Record<string, NavItem>;

// Compose is a button, never a destination. Team lives under Settings only.
const GROUPS: NavGroup[] = [
  { key: 'main', items: [ITEM.overview, ITEM.inbox] },
  { key: 'publish', labelKey: 'nav.groups.publish', items: [ITEM.calendar, ITEM.plan, ITEM.posts, ITEM.approvals] },
  { key: 'library', labelKey: 'nav.groups.library', items: [ITEM.media, ITEM.templates] },
  { key: 'insights', labelKey: 'nav.groups.insights', items: [ITEM.analytics, ITEM.feeds] },
  { key: 'brand', labelKey: 'nav.groups.brand', items: [ITEM.brand, ITEM.channels] },
];
const FOOTER: NavItem[] = [ITEM.settings, ITEM.docs];

/**
 * A client (CLIENT_VIEWER) gets a review portal, not the agency's workspace:
 * what waits for them, the calendar to read, and the results (PC-26).
 */
const CLIENT_GROUPS: NavGroup[] = [{ key: 'main', items: [ITEM.approvals, ITEM.calendar, ITEM.analytics] }];
const CLIENT_FOOTER: NavItem[] = [ITEM.docs];

const ADMIN_ITEM: NavItem = { key: 'admin', path: ROUTES.ADMIN, icon: Icons.admin, labelKey: 'nav.admin' };

/** Rail width when collapsed to icons; it is 232px with labels. */
export const RAIL_WIDTH = 72;

function Count({ count, tone }: { count: number; tone: 'accent' | 'warn' | 'bad' }) {
  if (count <= 0) return null;
  return (
    <span
      className="relative z-10 text-caption font-semibold px-[6px] py-[1px] rounded-full leading-[1.4] shrink-0 tabular-nums"
      style={
        tone === 'accent'
          ? { background: 'var(--color-accent)', color: '#fff' }
          : tone === 'bad'
            ? { background: 'var(--bad)', color: '#fff' }
            : { background: 'var(--warn-soft)', color: 'var(--color-warning-text)' }
      }
      aria-hidden="true"
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

interface ItemProps {
  item: NavItem;
  expanded: boolean;
  /** Small count shown beside the label when expanded. */
  count?: number;
  countTone?: 'accent' | 'warn' | 'bad';
  /** Red dot on the icon: something is escalated to me. Shown even when collapsed. */
  dot?: boolean;
  /** What the count and the dot say to a screen reader (they are aria-hidden). */
  srText?: string;
  onNavigate: () => void;
}

function Item({ item, expanded, count = 0, countTone = 'accent', dot, srText, onNavigate }: ItemProps) {
  const { t } = useTranslation();
  const reduced = useReducedMotion() ?? false;
  const Icon = item.icon;
  const label = t(item.labelKey);
  return (
    <NavLink
      to={item.path}
      onClick={onNavigate}
      title={expanded ? undefined : label}
      className={({ isActive }) =>
        [
          'relative flex items-center gap-3 rounded-[10px] no-underline w-full overflow-hidden',
          'min-h-[40px] pointer-coarse:min-h-[44px] text-ui',
          expanded ? 'px-3' : 'justify-center px-0',
          isActive ? 'text-[var(--ink)] font-semibold' : 'text-[var(--ink-2)] font-medium hover:bg-[var(--color-surface-3)] hover:text-[var(--ink)]',
        ].join(' ')
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <motion.span
              layoutId={LAYOUT_ID.sidebarActive}
              className="absolute inset-0 rounded-[10px] bg-[var(--color-accent-soft)]"
              transition={motionSafe(SPRING, reduced)}
              aria-hidden="true"
            />
          )}
          {isActive && (
            <span className="absolute start-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-e-full bg-[var(--color-accent)]" aria-hidden="true" />
          )}
          <span className="relative z-10 shrink-0 inline-flex">
            <Icon size={20} strokeWidth={1.5} aria-hidden="true" />
            {dot && <span data-testid="escalated-dot" className="absolute -top-0.5 -end-0.5 w-2 h-2 rounded-full bg-[var(--bad)] ring-2 ring-[var(--color-surface)]" aria-hidden="true" />}
            {!expanded && count > 0 && (
              <span className="absolute -top-1.5 -end-2 text-[10px] font-semibold px-[5px] rounded-full leading-[1.5] tabular-nums" style={{ background: 'var(--color-accent)', color: '#fff' }} aria-hidden="true">
                {count > 99 ? '99+' : count}
              </span>
            )}
          </span>
          {/* The label stays in the DOM so the link keeps its name; it is
              clipped by the rail width until the rail expands. */}
          {/* Collapsed: screen-reader text only. `relative` beside `sr-only` won the
              cascade, so the hidden label kept its width + gap and pushed the
              icon off-centre in the 72px rail. */}
          <span className={expanded ? 'relative z-10 flex-1 truncate whitespace-nowrap' : 'sr-only'}>{label}</span>
          {expanded && count > 0 && <Count count={count} tone={countTone} />}
          {srText && <span className="sr-only">{srText}</span>}
        </>
      )}
    </NavLink>
  );
}

/**
 * The app rail (blueprint C01, DS04): labelled groups — Home and Inbox, Publish,
 * Library, Insights, Brand — with Settings and Help at the foot. From 1280px
 * the labels show by default (232px) unless collapsed, which is remembered;
 * collapsed (and below 1280px) it is a 72px icon rail whose icons name
 * themselves in a tooltip — it never floats over the page, which used to
 * cover the content's left edge. The phone/tablet drawer always shows labels. The badge on
 * Inbox is the unread count, with a red dot when anything is escalated to me.
 */
export function Sidebar({ open, onClose, onCompose, onSearch, showPoweredBy = false }: SidebarProps) {
  const { t } = useTranslation();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const { unread, escalated } = useInboxBadges();
  const attentionCount = useAttentionCount();
  const { data: planData } = useCurrentPlan();
  const planOpen = openItems(planData?.plan).length;
  // Internal staff only. The route and every /admin API are guarded separately.
  const isSuperAdmin = useAuthStore((s) => s.user?.app_metadata?.['role'] === 'super_admin');
  // Viewers and clients write nothing: no Compose. Clients see the review portal (PC-26).
  const { canWrite, role } = useCanWrite();
  const client = role === 'client';
  // A role never sees a destination the API refuses it (AL-06): a client's
  // portal keeps approvals and the calendar; analytics only where the API serves it.
  const allowedFor = (item: NavItem): boolean => (item.key !== 'inbox' || canReadInbox(role)) && (item.key !== 'analytics' || canReadAnalytics(role));
  const groups = (client ? CLIENT_GROUPS : GROUPS)
    .map((g) => ({ ...g, items: g.items.filter(allowedFor) }))
    .filter((g) => g.items.length > 0);
  const footer = client ? CLIENT_FOOTER : FOOTER;

  // Docked with labels on a wide screen unless collapsed; labels in the drawer.
  // No hover/focus peek: it floated over the page and covered its left edge.
  const wide = useMatchMedia(WIDE_QUERY);
  const collapsed = useNavStore((s) => s.collapsed);
  const toggleCollapsed = useNavStore((s) => s.toggleCollapsed);
  const pinned = wide && !collapsed;
  const expanded = open || pinned;

  // Below lg the closed drawer is only moved off-screen: it is made inert so
  // its links leave the tab order and the accessibility tree. While open,
  // Escape closes it, focus moves into it, and returns to the opener after.
  const isDesktop = useMatchMedia(DESKTOP_QUERY);
  const asideRef = useRef<HTMLElement>(null);
  const hiddenDrawer = !isDesktop && !open;
  useEffect(() => {
    const el = asideRef.current;
    if (!el) return;
    if (hiddenDrawer) {
      el.setAttribute('inert', '');
      el.setAttribute('aria-hidden', 'true');
    } else {
      el.removeAttribute('inert');
      el.removeAttribute('aria-hidden');
    }
  }, [hiddenDrawer]);
  useEffect(() => {
    if (isDesktop || !open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const raf = requestAnimationFrame(() => asideRef.current?.querySelector<HTMLElement>('a[href], button')?.focus());
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKey);
      if (opener?.isConnected) opener.focus();
    };
  }, [open, isDesktop]);

  return (
    <aside
      ref={asideRef}
      id="app-sidebar"
      className={[
        'fixed start-0 top-[64px] bottom-0 bg-[var(--color-surface)] border-e border-[var(--line)] overflow-y-auto overflow-x-hidden z-[45] py-3 px-3 flex flex-col',
        'lg:translate-x-0',
        open ? 'translate-x-0' : 'max-lg:-translate-x-full rtl:max-lg:translate-x-full',
        'max-lg:w-[min(280px,80vw)]',
        expanded ? 'lg:w-[232px]' : 'lg:w-[72px]',
      ].join(' ')}
      style={{
        transition: 'transform var(--motion-panel), width var(--motion-micro)',
      }}
      data-expanded={expanded ? 'true' : 'false'}
      data-pinned={pinned ? 'true' : 'false'}
      aria-label={t('nav.sidebar')}
    >
      <nav aria-label={t('nav.main')} className="flex flex-col gap-0.5">
        {/* Phones have no search in the top bar (blueprint S90), so the drawer carries it. */}
        {onSearch && (
          <button
            type="button"
            onClick={() => { onClose(); onSearch(); }}
            className="md:hidden flex items-center gap-3 min-h-[44px] mb-1.5 px-3 rounded-[10px] border border-[var(--line)] bg-[var(--color-surface-2)] text-[var(--ink-3)] text-ui cursor-pointer w-full hover:border-[var(--line-2)]"
            aria-label={t('topbar.searchShortcut')}
          >
            <Icons.search size={20} className="shrink-0" strokeWidth={1.5} aria-hidden="true" />
            <span className="flex-1 text-start truncate">{t('topbar.search')}</span>
          </button>
        )}
        {groups.map((g) => {
          const headingId = `nav-group-${g.key}`;
          return (
            <div key={g.key} role="group" aria-labelledby={g.labelKey ? headingId : undefined} className="flex flex-col gap-0.5">
              {g.labelKey && (
                <div
                  id={headingId}
                  className={expanded ? 'eyebrow-mono px-3 pt-4 pb-1 truncate' : 'sr-only'}
                >
                  {t(g.labelKey)}
                </div>
              )}
              {/* Collapsed to icons, a hairline marks where a group starts. */}
              {g.labelKey && !expanded && <div className="mx-3 my-2 border-t border-[var(--line)]" aria-hidden="true" />}
              {g.items.map((item) => (
                <Item
                  key={item.key}
                  item={item}
                  expanded={expanded}
                  onNavigate={onClose}
                  count={item.key === 'inbox' ? unread : item.key === 'overview' ? attentionCount : item.key === 'plan' ? planOpen : 0}
                  countTone={item.key === 'overview' ? 'warn' : 'accent'}
                  dot={item.key === 'inbox' && escalated > 0}
                  srText={
                    item.key === 'inbox'
                      ? [unread > 0 ? t('nav.unreadCount', { count: unread }) : '', escalated > 0 ? t('nav.escalatedCount', { count: escalated }) : ''].filter(Boolean).join(', ') || undefined
                      : item.key === 'overview' && attentionCount > 0
                        ? t('nav.attentionCount', { count: attentionCount })
                        : undefined
                  }
                />
              ))}
            </div>
          );
        })}

        {/* Compose is reached from the top bar; only the drawer (phones and
            tablets) keeps a quiet copy, so desktop shows one Compose (DS03). */}
        {canWrite && <button
          type="button"
          onClick={() => { onClose(); onCompose(); }}
          title={expanded ? undefined : t('nav.compose')}
          className={[
            'lg:hidden flex items-center gap-3 min-h-[44px] my-1.5 rounded-[10px] border border-[var(--color-accent-border)] bg-[var(--color-accent-soft)] text-[var(--color-accent-text)] text-body font-semibold cursor-pointer w-full hover:border-[var(--color-accent)] overflow-hidden',
            expanded ? 'px-3' : 'justify-center px-0',
          ].join(' ')}
        >
          <Icons.add size={20} className="shrink-0" strokeWidth={1.5} aria-hidden="true" />
          <span className={expanded ? 'flex-1 text-start truncate' : 'sr-only'}>{t('nav.compose')}</span>
        </button>}
      </nav>

      <div className="flex-1 min-h-4" />

      <nav aria-label={t('nav.more')} className="flex flex-col gap-0.5 border-t border-[var(--line)] pt-3">
        {footer.map((item) => (
          <Item key={item.key} item={item} expanded={expanded} onNavigate={onClose} />
        ))}
        {isSuperAdmin && <Item item={ADMIN_ITEM} expanded={expanded} onNavigate={onClose} />}
        {/* Wide screens only: keep the labels, or fold the rail to icons. */}
        <button
          type="button"
          onClick={toggleCollapsed}
          className={['hidden xl:flex items-center gap-3 min-h-[40px] pointer-coarse:min-h-[44px] rounded-[10px] text-ui font-medium text-[var(--ink-3)] bg-transparent border-none cursor-pointer w-full hover:bg-[var(--color-surface-3)] hover:text-[var(--ink)]', expanded ? 'px-3' : 'justify-center px-0'].join(' ')}
          title={expanded ? undefined : t('nav.expand')}
        >
          {collapsed ? <Icons.panelExpand size={20} strokeWidth={1.5} className="shrink-0 rtl:flip" aria-hidden="true" /> : <Icons.panelCollapse size={20} strokeWidth={1.5} className="shrink-0 rtl:flip" aria-hidden="true" />}
          <span className={expanded ? 'flex-1 text-start truncate' : 'sr-only'}>{collapsed ? t('nav.expand') : t('nav.collapse')}</span>
        </button>
      </nav>

      {showPoweredBy && (
        <p
          data-testid="powered-by"
          className={['m-0 pt-3 px-3 text-caption text-[var(--ink-3)] truncate', expanded ? '' : 'sr-only'].join(' ')}
        >
          {t('branding.poweredByLine')}
        </p>
      )}
    </aside>
  );
}
